// Design-doc formats and their sections. A shared section catalog (each with a
// short guidance placeholder distilled from Will Wright's GDD template) is
// referenced by six built-in formats — from a one-page hook up to the full GDD.
// The formats are independent documents; the catalog just avoids re-describing
// the same section in six places. Both the catalog and the formats are editable
// through the Content Studio overlay (see designDocContentStore).

export interface DocSection {
  id: string;
  title: string;
  /** Placeholder guidance shown in the editor for an empty section. */
  guidance: string;
}

export interface DocFormat {
  id: string;
  name: string;
  blurb: string;
  /** Ordered section ids this format includes. */
  sectionIds: string[];
}

export const BASE_SECTIONS: DocSection[] = [
  { id: "title", title: "Working Title", guidance: "Your game's name. A working title is fine — it will evolve." },
  { id: "concept", title: "Concept", guidance: "An in-depth description that touches on player experience, mood, genre comparisons, market position, and the core mechanics that define the game." },
  { id: "logline", title: "Logline", guidance: "One or two sentences capturing the core experience, written from the player's perspective. Use clear nouns, strong verbs, and evocative adjectives." },
  { id: "keyFeatures", title: "Key Features", guidance: "The primary features that create your experience, with references to other games where they help." },
  { id: "playerExperience", title: "Player Experience", guidance: "The emotions, motivations, successes, failures, and overall impact the game generates for players." },
  { id: "mood", title: "Mood", guidance: "The atmosphere created by the aesthetics, gameplay, and narrative." },
  { id: "audience", title: "Audience", guidance: "A demographic and psychological description of the players you're trying to reach." },
  { id: "coreMechanics", title: "Core Mechanics", guidance: "The primary mechanics and systems that create the experience, with references to other games where they help." },
  { id: "elevatorPitch", title: "Elevator Pitch", guidance: "The shortest possible description of your core experience — 30 seconds to a minute." },
  { id: "hook", title: "The Hook", guidance: "Why this game, and why now — the single most compelling reason for someone to care." },
  { id: "marketPosition", title: "Market Position", guidance: "Where the game sits in the market: comparable titles, the gap it fills, and why it can succeed." },
  { id: "comparables", title: "Comparables & References", guidance: "Games (or films) that set expectations for tone, mechanics, or audience." },
  { id: "platform", title: "Platform", guidance: "The hardware the game runs on and why (PC, mobile, console), and what that means for your audience." },
  { id: "engine", title: "Engine", guidance: "The software you'll build in (Unity, Unreal, Godot, Game Maker, Twine, HTML5…) and why. Note the version." },
  { id: "savedData", title: "Saved Data", guidance: "What is saved locally versus on a server." },
  { id: "architecture", title: "Technical Architecture", guidance: "Key systems, the game loop, state management, and any notable technical risks." },
  { id: "revenueModel", title: "Revenue Model", guidance: "Payment method and pay structure (premium, free-to-play, subscription) and its impact on gameplay." },
  { id: "visualAesthetic", title: "Visual Aesthetic", guidance: "The visual style you're aiming for: 2-D or 3-D, pixel / low-poly / high-poly, palette, and the feelings it should evoke." },
  { id: "aestheticRefs", title: "Aesthetic References", guidance: "Images or a mood-board link that, together, evoke the target visual style." },
  { id: "conceptArt", title: "Concept Art", guidance: "Sketches of the key characters, enemies, and environments." },
  { id: "audioAesthetic", title: "Audio Aesthetic", guidance: "The sound palette you're aiming for; diegetic versus non-diegetic sound, and the emotions it supports." },
  { id: "audioRefs", title: "Audio References", guidance: "Tracks, and games or films whose sound design influences the project." },
  { id: "music", title: "Music", guidance: "A description and track list of the original music." },
  { id: "plotOverview", title: "Plot Overview", guidance: "A beat-by-beat description of the story, with a timeline if useful." },
  { id: "playerCharacters", title: "Player Characters", guidance: "The main characters players control." },
  { id: "otherCharacters", title: "Other Key Characters", guidance: "Other characters who populate the world." },
  { id: "teamRoster", title: "Team Roster", guidance: "Team members and their responsibilities — designer, programmer, artist, sound designer, and so on." },
  { id: "timeline", title: "Development Timeline", guidance: "Milestones and hard deadlines for the key features." },
  { id: "budget", title: "Budget", guidance: "Costs and revenues; balance it periodically to reflect the project's finances." },
  { id: "changelog", title: "Changelog", guidance: "Major changes to the design over time." },
  { id: "archivedFeatures", title: "Archived Features", guidance: "Cut features and material — often the seed of future games, expansions, and sequels." },

  // ---- Feature / System spec blocks ----
  { id: "premise", title: "Premise", guidance: "One line: what this is, and the feeling it should create for the player. State the target experience before the mechanics." },
  { id: "goalsNonGoals", title: "Goals & Non-Goals", guidance: "What this must accomplish — and, just as important, what it deliberately will not do. Non-goals keep scope honest." },
  { id: "anatomy", title: "Anatomy (Agents · Networks · Layers)", guidance: "The system's parts: agents (objects with their own behavior), networks (the paths they move along), and layers (global rules). At feature grain, the nouns, verbs, and adjectives." },
  { id: "coreLoop", title: "Core Loop", guidance: "The smallest repeatable cycle: the player's action, the feedback or reward it produces, and the reason to do it again." },
  { id: "rules", title: "Mechanics — if/then Rules", guidance: "The objective rules that govern behavior — the levers you author directly. Write them as 'if X, then Y' wherever you can." },
  { id: "dynamics", title: "Dynamics & Feedback Loops", guidance: "The run-time behavior that emerges as players operate the system — including the positive and negative feedback loops and any emergence you expect." },
  { id: "states", title: "States & Flow", guidance: "The states the system moves through and the transitions between them, including goal, fail, win, and lose states." },
  { id: "economy", title: "Economy (Sinks & Faucets)", guidance: "Resources, sinks, and faucets: what flows in, what drains it, and how you keep it balanced." },
  { id: "edgeCases", title: "Edge Cases & Exploits", guidance: "Degenerate strategies, exploits, dead-ends, and failure modes — how the system breaks, and what stops it." },
  { id: "dependencies", title: "Dependencies & Interfaces", guidance: "What this touches: UI, data, and the other systems or features it depends on or feeds into." },
  { id: "risks", title: "Risks & Open Questions", guidance: "The biggest unknowns and open questions still to resolve." },
  { id: "validation", title: "Validation Plan", guidance: "How you'll prototype and playtest this to know it works — the question each test answers." },
];

export const SECTION_BY_ID: Record<string, DocSection> = Object.fromEntries(
  BASE_SECTIONS.map((s) => [s.id, s])
);

export const BASE_FORMATS: DocFormat[] = [
  {
    id: "one-sheet",
    name: "One Sheet",
    blurb: "A single page that hooks the reader.",
    sectionIds: ["title", "logline", "keyFeatures", "mood", "audience", "elevatorPitch"],
  },
  {
    id: "ten-sheet",
    name: "Ten Sheet",
    blurb: "A ~10-page treatment of the core experience.",
    sectionIds: [
      "title", "concept", "logline", "keyFeatures", "playerExperience", "mood",
      "audience", "coreMechanics", "visualAesthetic", "audioAesthetic", "plotOverview", "elevatorPitch",
    ],
  },
  {
    id: "pitch",
    name: "Pitch",
    blurb: "Audience-shaped persuasion, led by the player's experience.",
    sectionIds: [
      "title", "logline", "hook", "playerExperience", "keyFeatures", "coreMechanics",
      "audience", "marketPosition", "comparables", "elevatorPitch",
    ],
  },
  {
    id: "technical-gdd",
    name: "Technical GDD",
    blurb: "The build view: platform, engine, data, architecture.",
    sectionIds: ["title", "platform", "engine", "savedData", "architecture", "revenueModel"],
  },
  {
    id: "small-gdd",
    name: "Small GDD",
    blurb: "A working subset for a small team.",
    sectionIds: [
      "title", "concept", "logline", "keyFeatures", "playerExperience", "coreMechanics",
      "platform", "visualAesthetic", "audioAesthetic", "teamRoster", "timeline",
    ],
  },
  {
    id: "feature-spec",
    name: "Feature Spec",
    blurb: "Conceptualize a single feature into a concrete spec.",
    sectionIds: [
      "title", "premise", "goalsNonGoals", "coreLoop", "rules", "states",
      "edgeCases", "dependencies", "risks", "validation",
    ],
  },
  {
    id: "system-spec",
    name: "System Spec",
    blurb: "Design an interacting system from anatomy to validation.",
    sectionIds: [
      "title", "premise", "goalsNonGoals", "anatomy", "coreLoop", "rules", "dynamics",
      "states", "economy", "edgeCases", "dependencies", "risks", "validation",
    ],
  },
  {
    id: "complete-gdd",
    name: "Complete GDD",
    blurb: "The full reference document across every section.",
    sectionIds: [
      "title", "concept", "logline", "keyFeatures", "playerExperience", "mood", "audience",
      "coreMechanics", "elevatorPitch", "platform", "engine", "savedData", "revenueModel",
      "visualAesthetic", "aestheticRefs", "conceptArt", "audioAesthetic", "audioRefs", "music",
      "plotOverview", "playerCharacters", "otherCharacters", "teamRoster", "timeline", "budget",
      "changelog", "archivedFeatures",
    ],
  },
];

/** Resolve a format's ordered sections against a (possibly overlaid) catalog. */
export function sectionsForFormat(
  format: DocFormat,
  sectionsById: Record<string, DocSection>
): DocSection[] {
  return format.sectionIds.map((id) => sectionsById[id]).filter((s): s is DocSection => Boolean(s));
}
